import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';

import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { STRIPE_INTRO_PAYMENT } from 'core/utils/stripe-payment';

import { useStore } from '../store/store';
import { getEnvUrl } from '../helpers/getEnvUrl';
import { applyFoodzeeAuthTokens } from '../helpers/applyFoodzeeAuthTokens';
import { buildPurchaseUserData } from '../helpers/buildPurchaseUserData';
import {
  CHECKOUT_POLL_INTERVAL_MS,
  CHECKOUT_POLL_TIMEOUT_MS,
  PAYMENT_SYSTEM_PROJECT,
  clearCheckoutSessionId,
  getCheckoutSessionStatus,
  readCheckoutSessionId,
} from '../helpers/checkoutApi';

const PAYWALL_PAGE = 'personal-plan-dynamic';
const EMAIL_PAGE = 'email-after-payment';
const UPSALE_PAGE = 'upsale-dynamic';

export type CheckoutStatusState = {
  /** Whether the blocking status overlay should be shown over the paywall. */
  visible: boolean;
  /** True once the poll timeout elapsed and we're still `pending`. */
  timedOut: boolean;
  /** Restart polling after a timeout. */
  retry: () => void;
};

/**
 * Stripe hosted-Checkout return handler, surfaced as a blocking overlay on the
 * paywall (replaces the old standalone `checkout-status` page). When the user
 * comes back from Stripe to `/checkout/success?session_id=...`, App routes to
 * the paywall with `checkoutSessionId` set; this hook polls the backend for the
 * session status and branches:
 *   - success + email present       → update-by-token (full purchase data) → upsell
 *   - success + requiresEmail:true  → email-after-payment (manual-email branch)
 *   - success (no email)            → upsell (report already emailed by backend)
 *   - pending                       → keep polling (overlay stays)
 *   - failed / in_progress          → hide overlay, stay on the paywall
 *   - poll timeout (still pending)  → "processing longer than usual" + retry
 *
 * No-op (visible:false, never polls) unless there's a pending checkout session,
 * so it has no effect on a normal paywall visit.
 */
export function useCheckoutStatusPolling(): CheckoutStatusState {
  const dispatch = useDispatch();

  const setPage = useStore((s) => s.setPage);
  const setEmail = useStore((s) => s.setEmail);
  const addVisitedPage = useStore((s) => s.addVisitedPage);
  const addSkippedPage = useStore((s) => s.addSkippedPage);
  const setCheckoutSessionId = useStore((s) => s.setCheckoutSessionId);
  const authTokens = useStore((s) => s.authTokens);
  const userId = useStore((s) => s.userId);

  const { sendEthnicity, routeGroups: flow } = useRemoteConfig();
  const { apiBaseUrl } = getEnvUrl();

  const sessionIdRef = useRef<string | null>(
    useStore.getState().checkoutSessionId || readCheckoutSessionId()
  );

  const [visible, setVisible] = useState<boolean>(!!sessionIdRef.current);
  const [timedOut, setTimedOut] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const finishedRef = useRef(false);

  // Re-establish the in-memory Foodzee/Stripe billing auth header after the
  // full-page reload caused by the Stripe redirect, so the one-click upsell on
  // the next page can charge. `reinitBilling: false` on purpose — the paywall's
  // own Plans layout already dispatches `init()` (products + config) on mount,
  // so re-fetching here would just duplicate `/payments/products` + `/config`.
  useEffect(() => {
    if (sessionIdRef.current && authTokens?.accessToken) {
      applyFoodzeeAuthTokens(
        {
          accessToken: authTokens.accessToken,
          refreshToken: authTokens.refreshToken,
          uid: authTokens.userId || userId,
        },
        dispatch,
        { reinitBilling: false, paymentSystem: 'stripe' }
      );
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the overlay and drop the stored session_id (failed / in_progress).
  const close = useCallback(() => {
    clearCheckoutSessionId();
    setCheckoutSessionId(null);
    setVisible(false);
  }, [setCheckoutSessionId]);

  const goToUpsell = useCallback(() => {
    close();
    addVisitedPage(PAYWALL_PAGE);
    addSkippedPage(EMAIL_PAGE);
    setPage(UPSALE_PAGE);
  }, [addSkippedPage, addVisitedPage, close, setPage]);

  const goToEmail = useCallback(
    (sessionId: string) => {
      // Keep the session_id in the store so the email page calls
      // /payments/checkout/manual-email instead of update-by-token.
      setCheckoutSessionId(sessionId);
      setVisible(false);
      addVisitedPage(PAYWALL_PAGE);
      setPage(EMAIL_PAGE);
    },
    [addVisitedPage, setCheckoutSessionId, setPage]
  );

  // Backend already collected the email (on Stripe). Attach the full onboarding
  // + subscription payload to the user via update-by-token, using that email —
  // identical to what the paywall card flow / email-after-payment page send.
  const sendUpdateByToken = useCallback(
    async (emailFromStatus: string) => {
      const currentUserId = userId || useStore.getState().userId;
      if (!currentUserId) return;

      try {
        const payload = buildPurchaseUserData({
          email: emailFromStatus,
          store: useStore.getState(),
          flow,
          sendEthnicity,
        });

        const response = await axios.put(
          `${apiBaseUrl}/user/update-by-token/${currentUserId}`,
          payload
        );

        Analytics.trackEvent('checkout', 'update_by_token_success');

        const { refreshToken, accessToken } = response.data || {};
        if (accessToken) {
          useStore.getState().setAuthTokens({
            refreshToken,
            accessToken,
            userId: currentUserId,
          });
        }
      } catch (error) {
        Analytics.trackEvent('checkout', 'update_by_token_error');
        console.error('Checkout: update-by-token failed', error);
      }
    },
    [apiBaseUrl, flow, sendEthnicity, userId]
  );

  const poll = useCallback(async () => {
    const sessionId = sessionIdRef.current;

    if (!sessionId) {
      setVisible(false);
      return;
    }

    if (Date.now() - startedAtRef.current > CHECKOUT_POLL_TIMEOUT_MS) {
      setTimedOut(true);
      return;
    }

    try {
      const { status, requiresEmail, email } = await getCheckoutSessionStatus(
        sessionId,
        PAYMENT_SYSTEM_PROJECT
      );

      if (finishedRef.current) return;

      switch (status) {
        case 'success':
          finishedRef.current = true;
          // The user has now actually entered and submitted their payment
          // details on the Stripe-hosted page and been returned to us — this is
          // the hosted-Checkout analog of submitting the in-app payment form, so
          // fire `modal_payment_submit` here (not when leaving for Stripe).
          Analytics.trackEvent('modal_payment', 'submit', {
            productId: STRIPE_INTRO_PAYMENT.productId,
            value: STRIPE_INTRO_PAYMENT.amount,
            currency: STRIPE_INTRO_PAYMENT.currency,
            paymentSystem: 'stripe',
            method: 'stripe_checkout',
          });
          // Fire the standard purchase event (ecommerce_purchase) instead of the
          // internal checkout_success, so the hosted-Checkout flow reports the
          // sale the same way the card flow does. The intro (today's) charge is
          // the transaction; session_id is the transaction id.
          Analytics.trackPurchaseEvent({
            transactionId: sessionId,
            productId: STRIPE_INTRO_PAYMENT.productId,
            revenue: STRIPE_INTRO_PAYMENT.amount,
            currency: STRIPE_INTRO_PAYMENT.currency,
            method: 'stripe_checkout',
            paymentSystem: 'stripe',
            email: email || undefined,
          });
          if (email) {
            // Email known — persist it to the store so the upsell page (e.g.
            // web-upsale-4) can display it, save the full purchase payload, then
            // go to upsell.
            setEmail(email);
            await sendUpdateByToken(email);
            goToUpsell();
          } else if (requiresEmail) {
            goToEmail(sessionId);
          } else {
            goToUpsell();
          }
          return;

        case 'failed':
          finishedRef.current = true;
          Analytics.trackEvent('checkout', 'failed');
          close();
          return;

        case 'in_progress':
          finishedRef.current = true;
          Analytics.trackEvent('checkout', 'in_progress');
          close();
          return;

        case 'pending':
        default:
          pollTimerRef.current = setTimeout(poll, CHECKOUT_POLL_INTERVAL_MS);
          return;
      }
    } catch (error) {
      if (finishedRef.current) return;
      // Network hiccup — keep trying until the overall timeout.
      pollTimerRef.current = setTimeout(poll, CHECKOUT_POLL_INTERVAL_MS);
    }
  }, [close, goToEmail, goToUpsell, sendUpdateByToken, setEmail]);

  useEffect(() => {
    if (!sessionIdRef.current) return;
    poll();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => {
    setTimedOut(false);
    finishedRef.current = false;
    startedAtRef.current = Date.now();
    poll();
  }, [poll]);

  return { visible, timedOut, retry };
}
